import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from 'shared/core';
import { AlertMessageService } from 'shared/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { environment } from 'src/environments/environment';
import { LowGradeReasonModel, Responsibility, Modules } from 'shared/models';

@Component({
    selector: 'app-low-grade-reason',
    templateUrl: './low-grade-reason.component.html',
    styleUrls: ['./low-grade-reason.component.css'],
    standalone: false
})
export class LowGradeReasonComponent {
    validateForm!: UntypedFormGroup;
    ApiUrl = environment.Api_Url;

    LowGradeReasonList: LowGradeReasonModel[] = [];
    LowGradeReasonListOriginal: LowGradeReasonModel[] = [];
    NewReason: LowGradeReasonModel = new LowGradeReasonModel();
    isVisible = false;
    isLoading = false;
    isTableLoading = false;
    PopUpTitle = 'Add New Low Grade Reason';
    searchValue = '';
    totalItemsCount = 0;

    permission = {
        View: false,
        Add: false,
        Edit: false,
        Delete: false
    };

    constructor(
        private fb: UntypedFormBuilder,
        public http: HttpClient,
        private router: Router,
        private auth: AuthService,
        private alertService: AlertMessageService,
        private modalService: NzModalService
    ) { }

    ngOnInit() {
        this.permission.View = this.auth.CheckResponsibility(Modules.ProductionLowGradeReason, Responsibility.View);
        this.permission.Add = this.auth.CheckResponsibility(Modules.ProductionLowGradeReason, Responsibility.Add);
        this.permission.Edit = this.auth.CheckResponsibility(Modules.ProductionLowGradeReason, Responsibility.Edit);
        this.permission.Delete = this.auth.CheckResponsibility(Modules.ProductionLowGradeReason, Responsibility.Delete);

        if (!this.permission.View) {
            this.router.navigate(['/home/unauthorized']);
            return;
        }
        this.GetLowGradeReasonList();
        this.validateForm = this.fb.group({
            ReasonName: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
            Description: [null, [Validators.required, Validators.maxLength(500)]]
        });
    }

    GetLowGradeReasonList() {
        this.isTableLoading = true;
        this.http.get<LowGradeReasonModel[]>(`${this.ApiUrl}production/getlowgradereasonlist`).subscribe({
            next: (res) => {
                this.LowGradeReasonList = res.map((item, index) => ({
                    ...item,
                    SerialNo: index + 1
                }));
                this.LowGradeReasonListOriginal = [...this.LowGradeReasonList];
                this.totalItemsCount = this.LowGradeReasonList.length;
                this.isTableLoading = false;
            },
            error: () => {
                this.alertService.error('Failed to load Low Grade Reasons.');
                this.isTableLoading = false;
            }
        });
    }

    showModal() {
        this.PopUpTitle = 'Add New Low Grade Reason';
        this.NewReason = new LowGradeReasonModel();
        this.validateForm.reset();
        this.isVisible = true;
    }

    OpenEditPop(data: LowGradeReasonModel) {
        this.PopUpTitle = 'Edit Low Grade Reason';
        this.NewReason = { ...data };
        this.validateForm.setValue({
            ReasonName: data.ReasonName,
            Description: data.Description
        });
        this.isVisible = true;
    }

    handleOk() {
        if (!this.validateForm.valid) {
            Object.values(this.validateForm.controls).forEach(ctrl => {
                ctrl.markAsDirty();
                ctrl.updateValueAndValidity();
            });
            return;
        }
        this.isLoading = true;
        const payload: LowGradeReasonModel = {
            ...this.NewReason,
            ReasonName: this.validateForm.value.ReasonName,
            Description: this.validateForm.value.Description
        };
        this.http.post(`${this.ApiUrl}production/addupdatelowgradereason`, payload).subscribe({
            next: () => {
                this.alertService.success('Low Grade Reason saved successfully.');
                this.isLoading = false;
                this.isVisible = false;
                this.GetLowGradeReasonList();
            },
            error: (err) => {
                this.alertService.error(err?.error ?? 'An error occurred while saving.');
                this.isLoading = false;
            }
        });
    }

    handleCancel() {
        this.isVisible = false;
        this.validateForm.reset();
    }

    handleDelete(id: number) {
        this.modalService.confirm({
            nzTitle: 'Are you sure you want to delete this Low Grade Reason?',
            nzOkText: 'Yes, Delete',
            nzOkDanger: true,
            nzOnOk: () => {
                this.http.post(`${this.ApiUrl}production/deletelowgradereason`, { LowGradeReasonId: id }).subscribe({
                    next: () => {
                        this.alertService.success('Low Grade Reason deleted successfully.');
                        this.GetLowGradeReasonList();
                    },
                    error: () => {
                        this.alertService.error('Failed to delete Low Grade Reason.');
                    }
                });
            }
        });
    }

    search() {
        const val = this.searchValue.toLowerCase().trim();
        if (!val) {
            this.LowGradeReasonList = [...this.LowGradeReasonListOriginal];
        } else {
            this.LowGradeReasonList = this.LowGradeReasonListOriginal.filter(
                x =>
                    x.ReasonName?.toLowerCase().includes(val) ||
                    x.ReasonCode?.toLowerCase().includes(val) ||
                    x.Description?.toLowerCase().includes(val)
            );
        }
        this.totalItemsCount = this.LowGradeReasonList.length;
    }

    reset() {
        this.searchValue = '';
        this.LowGradeReasonList = [...this.LowGradeReasonListOriginal];
        this.totalItemsCount = this.LowGradeReasonList.length;
    }

    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') this.search();
    }

    ValidateText() {
        if (!this.searchValue) this.reset();
    }
}
